import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Bell, BellOff, Clock, FileText, Scale, Settings, ArrowLeft,
  AlertTriangle, Calendar, CheckCircle2, ChevronRight, Loader2,
  Mail, Smartphone, Filter, RefreshCw, Eye, EyeOff, Trash2,
  Plus, X, Save, Info
} from 'lucide-react';

const SmartAlertsPage = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // overview, settings, legal, calendar
  
  // Alerts data
  const [alerts, setAlerts] = useState(null);
  const [settings, setSettings] = useState(null);
  const [legalCategories, setLegalCategories] = useState([]);
  const [expiringContracts, setExpiringContracts] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  
  // Admin state
  const [isAdmin, setIsAdmin] = useState(false);
  const [legalChanges, setLegalChanges] = useState([]);

  useEffect(() => {
    if (user) {
      setIsAdmin(user.email === 'mleroux@notariasanantonio.cl' || user.is_admin);
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [alertsRes, settingsRes, expiringRes, calendarRes] = await Promise.all([
        api.get('/smart-alerts/check'),
        api.get('/smart-alerts/settings'),
        api.get('/smart-alerts/expiring?days=30'),
        api.get('/smart-alerts/calendar')
      ]);
      
      setAlerts(alertsRes.data);
      setSettings(settingsRes.data.settings);
      setLegalCategories(settingsRes.data.legal_categories || []);
      setExpiringContracts(expiringRes.data.contracts || []);
      setCalendarEvents(calendarRes.data.events || []);
      
      if (isAdmin) {
        const legalRes = await api.get('/smart-alerts/admin/legal-changes');
        setLegalChanges(legalRes.data.changes || []);
      }
    } catch (error) {
      console.error('Error loading alerts:', error);
      toast.error('Error al cargar alertas');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/smart-alerts/settings', settings);
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const markLegalChangeSeen = async (changeId) => {
    try {
      await api.post(`/smart-alerts/legal-change/seen/${changeId}`);
      setAlerts(prev => ({
        ...prev,
        legal_changes: prev.legal_changes.filter(c => c.id !== changeId)
      }));
      toast.success('Marcado como visto');
    } catch (error) {
      toast.error('Error');
    }
  };

  const seedLegalChanges = async () => {
    try {
      const res = await api.post('/smart-alerts/admin/seed-legal-changes');
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error('Error al crear cambios legales');
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const toggleExpirationDay = (day) => {
    const days = settings?.expiration_days || [7, 3, 1];
    if (days.includes(day)) {
      updateSetting('expiration_days', days.filter(d => d !== day));
    } else {
      updateSetting('expiration_days', [...days, day].sort((a, b) => b - a));
    }
  };

  const toggleLegalCategory = (categoryId) => {
    const categories = settings?.legal_categories || [];
    if (categories.includes(categoryId)) {
      updateSetting('legal_categories', categories.filter(c => c !== categoryId));
    } else {
      updateSetting('legal_categories', [...categories, categoryId]);
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'high': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default: return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    }
  };

  const getImportanceColor = (importance) => {
    switch (importance) {
      case 'critical': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-black';
      default: return 'bg-slate-500 text-white';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen mesh-gradient flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen mesh-gradient pb-20">
      {/* Header */}
      <header className="glass sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="p-2 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl">
                <Bell className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Alertas Inteligentes</h1>
                <p className="text-sm text-slate-400">Vencimientos y cambios legales</p>
              </div>
            </div>
            
            {alerts?.summary?.total > 0 && (
              <div className="flex items-center space-x-2">
                <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm font-medium">
                  {alerts.summary.total} alertas
                </span>
              </div>
            )}
          </div>
          
          {/* Tabs */}
          <div className="mt-4 flex space-x-2 overflow-x-auto pb-2">
            {[
              { id: 'overview', label: 'Resumen', icon: Bell },
              { id: 'settings', label: 'Configuración', icon: Settings },
              { id: 'legal', label: 'Cambios Legales', icon: Scale },
              { id: 'calendar', label: 'Calendario', icon: Calendar }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg flex items-center space-x-2 whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-orange-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
                <Clock className="w-8 h-8 mx-auto text-orange-400 mb-2" />
                <p className="text-2xl font-bold">{alerts?.summary?.expiring_count || 0}</p>
                <p className="text-sm text-slate-400">Por Vencer</p>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
                <FileText className="w-8 h-8 mx-auto text-yellow-400 mb-2" />
                <p className="text-2xl font-bold">{alerts?.summary?.pending_signatures_count || 0}</p>
                <p className="text-sm text-slate-400">Firmas Pendientes</p>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
                <Scale className="w-8 h-8 mx-auto text-blue-400 mb-2" />
                <p className="text-2xl font-bold">{alerts?.summary?.legal_changes_count || 0}</p>
                <p className="text-sm text-slate-400">Cambios Legales</p>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
                <Bell className="w-8 h-8 mx-auto text-red-400 mb-2" />
                <p className="text-2xl font-bold">{alerts?.summary?.total || 0}</p>
                <p className="text-sm text-slate-400">Total Alertas</p>
              </div>
            </div>

            {/* Expiring Contracts */}
            {expiringContracts.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-orange-400" />
                  Contratos por Vencer
                </h2>
                <div className="space-y-3">
                  {expiringContracts.slice(0, 5).map(contract => (
                    <div
                      key={contract.id}
                      className={`p-4 rounded-xl border ${getUrgencyColor(contract.urgency)}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-medium">{contract.title}</h3>
                          <p className="text-sm opacity-80">
                            Vence en {contract.days_until_expiry} día{contract.days_until_expiry !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          contract.urgency === 'critical' ? 'bg-red-500 text-white' :
                          contract.urgency === 'high' ? 'bg-orange-500 text-white' :
                          'bg-yellow-500 text-black'
                        }`}>
                          {contract.urgency === 'critical' ? 'URGENTE' :
                           contract.urgency === 'high' ? 'PRONTO' : 'PRÓXIMO'}
                        </span>
                      </div>
                      <button
                        onClick={() => navigate(`/contracts/${contract.id}`)}
                        className="mt-3 text-sm flex items-center hover:underline"
                      >
                        Ver contrato <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Legal Changes */}
            {alerts?.legal_changes?.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <Scale className="w-5 h-5 mr-2 text-blue-400" />
                  Cambios Legales Relevantes
                </h2>
                <div className="space-y-3">
                  {alerts.legal_changes.map(change => (
                    <div
                      key={change.id}
                      className="bg-slate-800/50 border border-slate-700 rounded-xl p-4"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getImportanceColor(change.importance)}`}>
                            {change.importance?.toUpperCase()}
                          </span>
                          <span className="text-xs text-slate-400">{change.category}</span>
                        </div>
                        <button
                          onClick={() => markLegalChangeSeen(change.id)}
                          className="p-1 hover:bg-slate-700 rounded transition-colors"
                          title="Marcar como visto"
                        >
                          <Eye className="w-4 h-4 text-slate-400" />
                        </button>
                      </div>
                      <h3 className="font-medium mb-1">{change.title}</h3>
                      <p className="text-sm text-slate-400 mb-2">{change.description}</p>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>Vigente desde: {change.effective_date}</span>
                        {change.source && <span>{change.source}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {alerts?.summary?.total === 0 && (
              <div className="text-center py-12">
                <CheckCircle2 className="w-16 h-16 mx-auto text-green-500 mb-4" />
                <h3 className="text-xl font-semibold mb-2">¡Todo al día!</h3>
                <p className="text-slate-400">No tienes alertas pendientes</p>
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && settings && (
          <div className="space-y-6">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-6">Configuración de Alertas</h2>
              
              {/* Expiration Alerts */}
              <div className="mb-6 pb-6 border-b border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <Clock className="w-5 h-5 text-orange-400" />
                    <div>
                      <h3 className="font-medium">Alertas de Vencimiento</h3>
                      <p className="text-sm text-slate-400">Notificar antes de que expiren contratos</p>
                    </div>
                  </div>
                  <button
                    onClick={() => updateSetting('expiration_alerts', !settings.expiration_alerts)}
                    className={`p-2 rounded-lg transition-colors ${
                      settings.expiration_alerts ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-400'
                    }`}
                  >
                    {settings.expiration_alerts ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
                  </button>
                </div>
                
                {settings.expiration_alerts && (
                  <div className="ml-8">
                    <p className="text-sm text-slate-400 mb-2">Alertar con anticipación de:</p>
                    <div className="flex flex-wrap gap-2">
                      {[30, 14, 7, 3, 1].map(day => (
                        <button
                          key={day}
                          onClick={() => toggleExpirationDay(day)}
                          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            settings.expiration_days?.includes(day)
                              ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                              : 'bg-slate-700 text-slate-400'
                          }`}
                        >
                          {day} día{day !== 1 ? 's' : ''}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Legal Change Alerts */}
              <div className="mb-6 pb-6 border-b border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <Scale className="w-5 h-5 text-blue-400" />
                    <div>
                      <h3 className="font-medium">Alertas de Cambios Legales</h3>
                      <p className="text-sm text-slate-400">Notificar cambios relevantes a tus contratos</p>
                    </div>
                  </div>
                  <button
                    onClick={() => updateSetting('legal_change_alerts', !settings.legal_change_alerts)}
                    className={`p-2 rounded-lg transition-colors ${
                      settings.legal_change_alerts ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-400'
                    }`}
                  >
                    {settings.legal_change_alerts ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
                  </button>
                </div>
                
                {settings.legal_change_alerts && (
                  <div className="ml-8">
                    <p className="text-sm text-slate-400 mb-2">Categorías de interés (vacío = todas):</p>
                    <div className="flex flex-wrap gap-2">
                      {legalCategories.map(cat => (
                        <button
                          key={cat.id}
                          onClick={() => toggleLegalCategory(cat.id)}
                          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            settings.legal_categories?.length === 0 || settings.legal_categories?.includes(cat.id)
                              ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                              : 'bg-slate-700 text-slate-400'
                          }`}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Signature Alerts */}
              <div className="mb-6 pb-6 border-b border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5 text-yellow-400" />
                    <div>
                      <h3 className="font-medium">Alertas de Firmas Pendientes</h3>
                      <p className="text-sm text-slate-400">Recordar firmantes pendientes</p>
                    </div>
                  </div>
                  <button
                    onClick={() => updateSetting('signature_pending_alerts', !settings.signature_pending_alerts)}
                    className={`p-2 rounded-lg transition-colors ${
                      settings.signature_pending_alerts ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-400'
                    }`}
                  >
                    {settings.signature_pending_alerts ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
                  </button>
                </div>
                
                {settings.signature_pending_alerts && (
                  <div className="ml-8">
                    <label className="text-sm text-slate-400 block mb-2">
                      Recordar después de (horas):
                    </label>
                    <select
                      value={settings.signature_reminder_hours || 24}
                      onChange={(e) => updateSetting('signature_reminder_hours', parseInt(e.target.value))}
                      className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                    >
                      <option value={12}>12 horas</option>
                      <option value={24}>24 horas</option>
                      <option value={48}>48 horas</option>
                      <option value={72}>72 horas</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Notification Methods */}
              <div className="mb-6">
                <h3 className="font-medium mb-4">Métodos de Notificación</h3>
                <div className="space-y-3">
                  <label className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg cursor-pointer">
                    <div className="flex items-center space-x-3">
                      <Smartphone className="w-5 h-5 text-purple-400" />
                      <span>Notificaciones Push</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.push_notifications}
                      onChange={(e) => updateSetting('push_notifications', e.target.checked)}
                      className="w-5 h-5 rounded"
                    />
                  </label>
                  <label className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg cursor-pointer">
                    <div className="flex items-center space-x-3">
                      <Mail className="w-5 h-5 text-cyan-400" />
                      <span>Notificaciones por Email</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.email_notifications}
                      onChange={(e) => updateSetting('email_notifications', e.target.checked)}
                      className="w-5 h-5 rounded"
                    />
                  </label>
                </div>
              </div>

              {/* Save Button */}
              <button
                onClick={saveSettings}
                disabled={saving}
                className="w-full py-3 bg-orange-600 hover:bg-orange-500 disabled:opacity-50 rounded-xl font-medium transition-colors flex items-center justify-center space-x-2"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Save className="w-5 h-5" />
                )}
                <span>{saving ? 'Guardando...' : 'Guardar Configuración'}</span>
              </button>
            </div>
          </div>
        )}

        {/* Legal Changes Tab */}
        {activeTab === 'legal' && (
          <div className="space-y-6">
            {isAdmin && (
              <div className="flex justify-end">
                <button
                  onClick={seedLegalChanges}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Crear Cambios de Ejemplo</span>
                </button>
              </div>
            )}

            {legalChanges.length > 0 ? (
              <div className="space-y-4">
                {legalChanges.map(change => (
                  <div
                    key={change.id}
                    className="bg-slate-800/50 border border-slate-700 rounded-xl p-5"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getImportanceColor(change.importance)}`}>
                          {change.importance?.toUpperCase()}
                        </span>
                        <span className="px-2 py-0.5 bg-slate-700 rounded text-xs">
                          {legalCategories.find(c => c.id === change.category)?.name || change.category}
                        </span>
                      </div>
                      <span className="text-xs text-slate-500">
                        Vigente: {change.effective_date}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{change.title}</h3>
                    <p className="text-slate-400 mb-3">{change.description}</p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {change.affected_contract_types?.map(type => (
                        <span key={type} className="px-2 py-1 bg-slate-700 rounded text-xs">
                          {type}
                        </span>
                      ))}
                    </div>
                    {change.source && (
                      <p className="text-xs text-slate-500">Fuente: {change.source}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Scale className="w-16 h-16 mx-auto text-slate-600 mb-4" />
                <p className="text-slate-400">No hay cambios legales registrados</p>
                {isAdmin && (
                  <button
                    onClick={seedLegalChanges}
                    className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm"
                  >
                    Crear cambios de ejemplo
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <div className="space-y-6">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                Próximos Eventos
              </h2>
              
              {calendarEvents.length > 0 ? (
                <div className="space-y-3">
                  {calendarEvents
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .slice(0, 15)
                    .map(event => (
                      <div
                        key={event.id}
                        className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: event.color }}
                          />
                          <div>
                            <p className="font-medium text-sm">{event.title}</p>
                            <p className="text-xs text-slate-400">
                              {event.type === 'expiration' ? 'Vencimiento' : 'Cambio Legal'}
                            </p>
                          </div>
                        </div>
                        <span className="text-sm text-slate-400">
                          {new Date(event.date).toLocaleDateString('es-CL')}
                        </span>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-center text-slate-400 py-8">
                  No hay eventos próximos
                </p>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SmartAlertsPage;
